import type { Step as RcStep } from '@sb/remote-control/types';

import { type RoutineView, type RoutineViewStep } from '../types';

type UiNode = RcStep.ConvertedSummary;

function flattenSteps(
  nodes: UiNode[] | undefined | null,
  acc: RoutineViewStep[] = [],
  inheritedSuppressed: boolean = false,
  stepConfigurations?: Record<string, { suppressed?: boolean }>,
): RoutineViewStep[] {
  if (!Array.isArray(nodes)) return acc;

  for (const node of nodes) {
    const id: string = (node as { id?: string }).id ?? '';

    const stepKind: string = (node as { stepKind?: string }).stepKind ?? '';

    const nodeWithSteps = node as { steps?: UiNode[] };

    const childrenNodes: UiNode[] = Array.isArray(nodeWithSteps.steps)
      ? nodeWithSteps.steps!
      : [];

    const childrenIds = childrenNodes
      .map((c) => (typeof c?.id === 'string' ? c.id : undefined))
      .filter((x): x is string => typeof x === 'string');

    // Suppression lives on the step configuration in the storage/UI shape;
    // the step tree node never carries the flag.
    const suppressed = id
      ? stepConfigurations?.[id]?.suppressed ?? false
      : false;

    // Derived: this step is effectively suppressed when it or any ancestor
    // has `suppressed: true`. The data model only stores the flag at the
    // toggled node; the DFS naturally carries the inherited state via the
    // recursion, so we materialize it here in a single pass.
    const effectivelySuppressed = suppressed || inheritedSuppressed;

    if (id && stepKind) {
      acc.push({
        id,
        stepKind,
        children: childrenIds,
        ...(suppressed ? { suppressed } : {}),
        ...(effectivelySuppressed ? { effectivelySuppressed } : {}),
      });
    }

    flattenSteps(childrenNodes, acc, effectivelySuppressed, stepConfigurations);
  }

  return acc;
}

function buildDerived(steps: RoutineViewStep[]): {
  stepById: Record<string, RoutineViewStep>;
  parentsById: Record<string, Array<{ id: string; stepKind: string }>>;
} {
  const stepById: Record<string, RoutineViewStep> = {};
  for (const s of steps) stepById[s.id] = s;

  // Build parent chains by traversing from each node upward by scanning children
  const childrenOf: Record<string, string[]> = {};
  for (const s of steps) childrenOf[s.id] = s.children ?? [];

  const parentsById: Record<
    string,
    Array<{ id: string; stepKind: string }>
  > = {};

  for (const s of steps) {
    const chain: Array<{ id: string; stepKind: string }> = [];
    let currentId = s.id;

    while (true) {
      let parentId: string | undefined;

      for (const [pid, kids] of Object.entries(childrenOf)) {
        if (kids.includes(currentId)) {
          parentId = pid;
          break;
        }
      }

      if (!parentId) break;
      const pn = stepById[parentId];
      if (!pn) break;
      chain.push({ id: pn.id, stepKind: pn.stepKind });
      currentId = parentId;
    }

    parentsById[s.id] = chain;
  }

  return { stepById, parentsById };
}

function ensureArgumentKinds(
  steps: RoutineViewStep[],
  stepConfigurations: Record<string, { stepKind: string; args: unknown }>,
): RoutineView['stepConfigurations'] {
  const out: RoutineView['stepConfigurations'] = {};

  for (const s of steps) {
    const cfg = stepConfigurations[s.id] ?? { stepKind: s.stepKind, args: {} };
    const args = cfg?.args;

    const isObj =
      args != null && typeof args === 'object' && !Array.isArray(args);

    const hasArgumentKind = isObj && (args as any).argumentKind != null;

    out[s.id] = {
      stepKind: cfg.stepKind || s.stepKind,
      args: (hasArgumentKind
        ? args
        : {
            argumentKind: s.stepKind,
            ...(isObj ? (args as object) : {}),
          }) as RcStep.Arguments,
    };
  }

  return out;
}

type UiRoutineLike = {
  id?: string;
  steps?: RcStep.ConvertedSummary[];
  stepConfigurations?: RoutineView['stepConfigurations'];
  environmentVariables?: Array<{ id: string; name: string }>;
  space?: RoutineView['space'];
};

export function normalizeFromRemoteControlUI(
  routine: UiRoutineLike,
): RoutineView {
  const rawConfigs = (routine?.stepConfigurations ?? {}) as Record<
    string,
    { stepKind: string; args: unknown; suppressed?: boolean }
  >;

  const steps = flattenSteps(
    Array.isArray(routine?.steps) ? routine.steps : [],
    [],
    false,
    rawConfigs,
  );

  const envVars = Array.isArray(routine?.environmentVariables)
    ? routine.environmentVariables
    : [];

  const space = Array.isArray(routine?.space) ? routine.space : [];

  const stepConfigurations = ensureArgumentKinds(steps, rawConfigs);
  const derived = buildDerived(steps);

  return {
    routineId: typeof routine?.id === 'string' ? routine.id : 'unknown',
    steps,
    stepConfigurations,
    space,
    environmentVariables: envVars,
    derived,
  };
}
